import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Store, Copy, Share2, Award, Coins, CheckCircle2, AlertTriangle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const MyStore = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await api.get('/store/my-sales'); setData(res.data); }
      catch { toast.error('Could not load your store'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <main className="max-w-4xl mx-auto p-6 text-center text-[var(--bz-muted)]">Loading…</main>;

  if (data && !data.can_sell) {
    return (
      <main className="max-w-2xl mx-auto p-6" data-testid="mystore-locked">
        <Card className="p-8 text-center">
          <Store className="h-10 w-10 mx-auto text-[#1D4ED8] mb-3" />
          <h1 className="text-xl font-black display">Start selling on INDIVALIFE</h1>
          <p className="text-sm text-[var(--bz-muted)] mt-2">Only network members and store purchasers can resell products and earn BV &amp; cashback. Join the network to unlock your store.</p>
          <Button className="mt-4 bg-[#1D4ED8] hover:bg-[#1E3A8A]" onClick={() => window.location.href = '/network'} data-testid="mystore-join-btn">Join the network</Button>
        </Card>
      </main>
    );
  }

  const storeLink = `${window.location.origin}/store?ref=${data.personal_id}`;
  const copyLink = () => { navigator.clipboard.writeText(storeLink); toast.success('Store link copied'); };
  const shareLink = () => {
    const text = `Shop genuine products on my INDIVALIFE store:\n${storeLink}`;
    if (navigator.share) { navigator.share({ title: 'My INDIVALIFE Store', text, url: storeLink }).catch(() => {}); }
    else window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <main className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5" data-testid="mystore-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-black display text-[#1E3A8A]">My Store</h1>
        <p className="text-sm text-[var(--bz-muted)]">Share your store link · earn commission &amp; BV on every sale</p>
      </div>

      {/* Activation status */}
      <Card className={`p-4 border-2 ${data.id_active ? 'border-blue-300 bg-blue-50' : 'border-amber-300 bg-amber-50'}`} data-testid="activation-card">
        {data.id_active ? (
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <b>ID active</b> — volume income is enabled{data.active_until ? ` until ${String(data.active_until).slice(0, 10)}` : ''}.
              <div className="text-[11px] text-[var(--bz-muted)] mt-1">Give a referral or complete 7 BV in {data.window_days} days to refresh your 28-day window.</div>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <b>ID inactive</b> — you still earn commission on sales, but <b>BV volume income is paused</b>.
              <div className="text-[11px] text-[var(--bz-muted)] mt-1">Activate for 28 days by giving a referral or completing <b>{data.bv_to_activate} more BV</b> within {data.window_days} days (held BV: {data.held_bv}).</div>
            </div>
          </div>
        )}
      </Card>

      {/* Resell link */}
      <Card className="p-4">
        <div className="font-bold display mb-2 flex items-center gap-2"><Store className="h-4 w-4 text-[#1D4ED8]" /> Your store link</div>
        <div className="flex gap-2 flex-wrap">
          <Input readOnly value={storeLink} className="flex-1 min-w-0 font-mono-bz text-xs" data-testid="store-link-input" />
          <Button variant="outline" onClick={copyLink} data-testid="copy-store-link"><Copy className="h-4 w-4 mr-1" />Copy</Button>
          <Button className="bg-[#25D366] hover:bg-[#1da851] text-white" onClick={shareLink} data-testid="share-store-link"><Share2 className="h-4 w-4 mr-1" />Share</Button>
        </div>
        <p className="text-[11px] text-[var(--bz-muted)] mt-2">Anyone who buys via your link earns you commission &amp; BV. Share individual products from the Store too.</p>
      </Card>

      {/* Earnings */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Total sales</div><div className="text-xl font-black display" data-testid="stat-sales">{data.sales.length}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Commission earned</div><div className="text-xl font-black display text-amber-700" data-testid="stat-cashback">{money(data.total_cashback)}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Volume BV</div><div className="text-xl font-black display text-[#1E3A8A]" data-testid="stat-volume-bv">{data.volume_bv}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">Held BV</div><div className="text-xl font-black display text-red-600" data-testid="stat-held-bv">{data.held_bv}</div></Card>
      </div>

      {/* Sales list */}
      <Card className="p-4">
        <div className="font-bold display mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#1D4ED8]" /> Recent sales</div>
        {data.sales.length === 0 ? (
          <div className="text-sm text-[var(--bz-muted)] text-center py-6" data-testid="sales-empty">No sales yet — share your store link to start earning.</div>
        ) : (
          <div className="space-y-2">
            {data.sales.map(s => (
              <div key={s.store_order_id} className="flex items-center justify-between gap-3 border rounded-md px-3 py-2" data-testid={`sale-${s.store_order_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{(s.lines || []).map(l => `${l.title} ×${l.qty}`).join(', ')}</div>
                  <div className="text-[11px] text-[var(--bz-muted)]">{String(s.created_at).slice(0, 10)} · {money(s.total)}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Badge className="bg-blue-100 text-blue-800 gap-0.5"><Award className="h-3 w-3" />{s.total_bv} BV</Badge>
                  <Badge className="bg-amber-100 text-amber-800 gap-0.5"><Coins className="h-3 w-3" />{money(s.total_cashback)}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </main>
  );
};

export default MyStore;
